<script>
  import { onMount } from "svelte";
  import { DataTable, Pagination } from "carbon-components-svelte";
  import {
    BadgeColor,
    InfoBlock,
    SectionWrapper,
    Select,
  } from "paxray-components-svelte";
  import Button from "lib/components/Button.svelte";
  import { fetchWithSession } from "lib/util/fetchWithSession.js";
  import GroupCell from "../../settings/components/GroupCell.svelte";
  import SearchInput from "../../settings/components/SearchInput.svelte";
  import ImportTeamUsersModal from "../../damprojects-frontend/components/ImportTeamUsersModal.svelte";

  const headers = [
    { key: "status", value: "Status" },
    { key: "first_name", value: "First Name" },
    { key: "last_name", value: "Last Name" },
    { key: "name", value: "System Login Name" },
    { key: "email", value: "E-Mail" },
    { key: "domain", value: "Domain" },
    { key: "team", value: "Assigned Team" },
    { key: "project", value: "Project" },
    { key: "lastupdate", value: "Last Contact" },
  ];
  const statusOptions = [
    { value: "Online", text: "Online" },
    { value: "Offline", text: "Offline" },
  ];
  const completenessOptions = [
    { value: "missing-any", text: "Any information missing" },
    { value: "missing-first-name", text: "First name missing" },
    { value: "missing-last-name", text: "Last name missing" },
    { value: "missing-email", text: "E-mail address missing" },
    { value: "complete", text: "All information complete" },
  ];

  let clients = [];
  let loading = true;
  let error = "";
  let searchClient = "";
  let selectedDomain = "";
  let selectedStatus = "";
  let selectedCompleteness = "";
  let page = 1;
  let pageSize = 10;
  let importOpen = false;
  let importing = false;
  let importMessage = "";
  let importMessageType = "hint";

  $: normalizedSearch = searchClient.trim().toLowerCase();
  $: domainOptions = [
    ...uniqueValues(
      clients.map((client) => client.domain).filter((domain) => domain !== "-"),
    )
      .sort((left, right) => left.localeCompare(right))
      .map((domain) => ({ value: domain, text: domain })),
    ...(clients.some((client) => client.domain === "-")
      ? [{ value: "__missing_domain__", text: "No domain" }]
      : []),
  ];
  $: filteredClients = clients.filter(
    (client) =>
      matchesSearch(client, normalizedSearch) &&
      matchesDomain(client, selectedDomain) &&
      (!selectedStatus || client.status === selectedStatus) &&
      matchesCompleteness(client, selectedCompleteness),
  );
  $: visibleClients = filteredClients.slice(
    (page - 1) * pageSize,
    page * pageSize,
  );
  $: if (page > Math.max(Math.ceil(filteredClients.length / pageSize), 1)) {
    page = 1;
  }

  onMount(loadClients);

  function onSearch(event) {
    searchClient = event.detail || "";
    page = 1;
  }

  function resetPage() {
    page = 1;
  }

  function matchesSearch(client, search) {
    if (!search) return true;
    return [
      client.status,
      client.first_name,
      client.last_name,
      client.name,
      client.email,
      client.domain,
      ...client.team.map((item) => item.name),
      ...client.project.map((item) => item.name),
      client.lastupdate,
    ].some((value) =>
      String(value || "")
        .toLowerCase()
        .includes(search),
    );
  }

  function matchesDomain(client, domain) {
    if (!domain) return true;
    return domain === "__missing_domain__"
      ? client.domain === "-"
      : client.domain === domain;
  }

  function matchesCompleteness(client, completeness) {
    if (!completeness) return true;
    if (completeness === "missing-any") {
      return (
        client.missingFirstName || client.missingLastName || client.missingEmail
      );
    }
    if (completeness === "missing-first-name") return client.missingFirstName;
    if (completeness === "missing-last-name") return client.missingLastName;
    if (completeness === "missing-email") return client.missingEmail;
    if (completeness === "complete") {
      return (
        !client.missingFirstName &&
        !client.missingLastName &&
        !client.missingEmail
      );
    }
    return true;
  }

  async function fetchJson(url) {
    const response = await fetchWithSession(url);
    if (!response.ok)
      throw new Error("Client information could not be loaded.");
    return response.json();
  }

  async function loadClients() {
    loading = true;
    error = "";

    try {
      const [users, projectGroups] = await Promise.all([
        fetchJson("/api/damprojects/getDAMTeamUsers"),
        fetchJson("/api/damprojects/projects"),
      ]);
      const projects = uniqueProjects([
        ...(projectGroups.analysis || []),
        ...(projectGroups.running || []),
      ]);
      const teamGroups = await Promise.all(
        projects.map(async (project) => ({
          project,
          teams: await fetchJson(
            `/api/damprojects/dam-teams?idproject=${encodeURIComponent(project.id)}`,
          ),
        })),
      );
      const assignments = buildAssignments(teamGroups);
      const projectsById = new Map(
        projects.map((project) => [String(project.id), project.name]),
      );

      clients = users.map((user) =>
        buildClientRow(user, assignments, projectsById),
      );
    } catch (exception) {
      error = exception.message;
      clients = [];
    } finally {
      loading = false;
    }
  }

  async function importClients(event) {
    importing = true;
    importMessage = "";

    try {
      const response = await fetchWithSession(
        "/api/damprojects/admin/dam-users/import",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ rows: event.detail.rows || [] }),
          suppressSuccessToast: true,
        },
      );
      const contentType = response.headers.get("content-type") || "";
      if (!contentType.toLowerCase().includes("application/json")) {
        throw new Error(
          "The user information service is not available yet. Please restart or update the DAM Projects backend and try again.",
        );
      }

      let payload;
      try {
        payload = await response.json();
      } catch {
        throw new Error("The import service returned an invalid response.");
      }
      if (!response.ok || !payload.ok) {
        throw new Error(
          payload.message || "Client information could not be imported.",
        );
      }

      const result = payload.data;
      const issuePreview = (result.issues || [])
        .slice(0, 3)
        .map((issue) => `Row ${issue.row}: ${issue.message}`)
        .join(" ");
      const summary =
        [
          `updated ${result.updated}`,
          `unchanged ${result.unchanged}`,
          `skipped ${result.skipped}`,
        ].join(", ") + ".";
      importMessage = issuePreview ? `${summary} ${issuePreview}` : summary;
      importMessageType = result.skipped ? "warning" : "hint";
      importOpen = false;
      await loadClients();
    } catch (exception) {
      importMessage =
        exception.message || "Client information could not be imported.";
      importMessageType = "warning";
    } finally {
      importing = false;
    }
  }

  function uniqueProjects(projects) {
    return [
      ...new Map(
        projects
          .filter((project) => project.id)
          .map((project) => [project.id, project]),
      ).values(),
    ];
  }

  function buildAssignments(teamGroups) {
    const assignments = new Map();

    for (const { project, teams } of teamGroups) {
      for (const team of teams) {
        for (const user of team.damUsers || []) {
          const userId = String(user.id || user);
          const current = assignments.get(userId) || [];
          current.push({ team: team.name, project: project.name });
          assignments.set(userId, current);
        }
      }
    }

    return assignments;
  }

  function buildClientRow(user, assignments, projectsById) {
    const rawName = String(user.name || "");
    const nameParts = rawName.split("\\");
    const hasDomain = nameParts.length > 1;
    const userAssignments = assignments.get(String(user.id)) || [];
    const teamNames = uniqueValues(userAssignments.map((item) => item.team));
    const projectNames = uniqueValues(
      userAssignments.map((item) => item.project),
    );
    const fallbackProject = projectsById.get(String(user.idproject));
    const firstName = String(user.first_name || user.firstName || "").trim();
    const lastName = String(user.last_name || user.lastName || "").trim();
    const email = String(user.email || "").trim();

    return {
      id: user.id,
      status: getStatus(user.lastupdate),
      first_name: firstName || "-",
      last_name: lastName || "-",
      name: hasDomain
        ? nameParts.slice(1).join("\\").replace(/^\\+/, "")
        : rawName,
      email: email || "-",
      domain: hasDomain && nameParts[0] ? nameParts[0] : "-",
      missingFirstName: !firstName,
      missingLastName: !lastName,
      missingEmail: !email,
      team: teamNames.map((name) => ({ name })),
      project: (projectNames.length
        ? projectNames
        : [fallbackProject].filter(Boolean)
      ).map((name) => ({ name })),
      lastupdate: formatDate(user.lastupdate),
    };
  }

  function uniqueValues(values) {
    return [...new Set(values.filter(Boolean))];
  }

  function getStatus(value) {
    const lastContact = new Date(value).getTime();
    const age = Date.now() - lastContact;
    return Number.isFinite(lastContact) && age >= 0 && age <= 60 * 60 * 1000
      ? "Online"
      : "Offline";
  }

  function formatDate(value) {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    const pad = (part) => String(part).padStart(2, "0");
    const formattedDate = `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${String(date.getFullYear()).slice(-2)}`;
    const isOlderThanSevenDays =
      Date.now() - date.getTime() > 7 * 24 * 60 * 60 * 1000;

    return isOlderThanSevenDays
      ? formattedDate
      : `${formattedDate} • ${pad(date.getHours())}:${pad(date.getMinutes())}`;
  }
</script>

<SectionWrapper fullscreen shrinkContent embedded>
  <section slot="main">
    <div class="embedded-toolbar">
      <div class="filter-field">
        <Select
          labelText="Domains"
          items={domainOptions}
          selected={selectedDomain}
          fitContent
          size="xl"
          on:update={(event) => {
            selectedDomain = event.detail;
            resetPage();
          }}
          color="dark"
          placeholder="All domains"
        />
      </div>
      <div class="filter-field">
        <Select
          labelText="Information"
          items={completenessOptions}
          selected={selectedCompleteness}
          fitContent
          size="xl"
          on:update={(event) => {
            selectedCompleteness = event.detail;
            resetPage();
          }}
          color="dark"
          placeholder="All client data"
        />
      </div>
      <div class="filter-field">
        <Select
          labelText="Status"
          items={statusOptions}
          selected={selectedStatus}
          fitContent
          size="xl"
          on:update={(event) => {
            selectedStatus = event.detail;
            resetPage();
          }}
          color="dark"
          placeholder="All statuses"
        />
      </div>
      <div class="filter-field search-field">
        <span class="filter-label">Search</span>
        <SearchInput placeholder="Search clients..." on:search={onSearch} />
      </div>
      <Button
        text="Update User Information (xlsx)"
        variant="secondary"
        iconLeft
        icon="upload"
        disabled={importing}
        on:click={() => (importOpen = true)}
      />
    </div>

    {#if importMessage}
      {#key importMessage}
        <InfoBlock
          type={importMessageType}
          heading={importMessageType === "hint"
            ? "Import completed"
            : "Import result"}
          message={importMessage}
        />
      {/key}
    {/if}

    {#if loading}
      <p>Loading clients...</p>
    {:else if error}
      <p class="error">{error}</p>
    {:else if !clients.length}
      <p>No clients have registered yet.</p>
    {:else if !filteredClients.length}
      <p>No clients match the selected filters.</p>
    {:else}
      <DataTable {headers} rows={visibleClients} class="custom-table">
        <svelte:fragment slot="cell" let:row let:cell>
          {#if cell.key === "team" || cell.key === "project"}
            <GroupCell {cell} />
          {:else if cell.key === "status"}
            <span
              class="client-status-pill"
              class:online={cell.value === "Online"}
              class:offline={cell.value === "Offline"}
            >
              <BadgeColor text={cell.value} color="client-status" small />
            </span>
          {:else if cell.key === "lastupdate"}
            {#if cell.value?.includes(" • ")}
              {cell.value.split(" • ")[0]}
              <strong class="last-contact-separator">•</strong>
              {cell.value.split(" • ")[1]}
            {:else}
              {cell.value || "-"}
            {/if}
          {:else}
            {cell?.value || "-"}
          {/if}
        </svelte:fragment>
      </DataTable>
      <Pagination
        bind:page
        bind:pageSize
        totalItems={filteredClients.length}
        pageSizes={[10, 25, 50]}
      />
    {/if}
  </section>
</SectionWrapper>

<ImportTeamUsersModal
  bind:open={importOpen}
  loading={importing}
  includeTeam={false}
  existingUsersOnly
  heading="Update User Information"
  projectName="Client_Management"
  on:submit={importClients}
  on:close={() => (importOpen = false)}
/>

<style>
  section {
    width: 100%;
  }

  .embedded-toolbar {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    width: 100%;
    gap: var(--control-gap);
    margin-bottom: var(--control-gap) !important;
  }

  .filter-field {
    display: flex;
    min-width: 0;
    flex-direction: column;
    gap: 0.25rem;
  }

  .filter-label {
    color: var(--pax-black);
    font-size: 0.75rem;
    font-weight: 400;
  }

  .search-field {
    flex: 1;
    min-width: 12rem;
  }

  .search-field :global(.search-input) {
    border-color: var(--pax-black);
  }

  .embedded-toolbar :global(button) {
    align-self: flex-end;
  }

  .error {
    color: var(--pax-red);
  }

  .client-status-pill.online {
    --custom-color: var(--pax-green);
  }

  .client-status-pill.offline {
    --custom-color: var(--pax-red);
  }

  .last-contact-separator {
    color: var(--pax-black);
    font-weight: 700;
  }
</style>
